import React, { useState, useEffect } from 'react';
import { Alert, Modal, Form, Button, ProgressBar, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useWriteContract } from 'wagmi';
import contractABI from '../../contracts/tgetherMembersInfo.json';

const contractAddress = process.env.REACT_APP_MEMBERS_INFO_CONTRACT_ADDRESS as `0x${string}`;
const totalSteps = 5;

interface SetProfileModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
}

const SetProfileModal: React.FC<SetProfileModalProps> = ({ show, setShow }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false); // New state for success alert
  const [error, setError] = useState(''); // Error state for better user feedback
  const [createFormData, setCreateFormData] = useState({
    name: '',
    description: '',
    degrees: '',
    awards: '',
    endpoint: ''
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (!show) {
      // Reset form and error state when modal is closed
      setCurrentStep(1);
      setCreateFormData({
        name: '',
        description: '',
        degrees: '',
        awards: '',
        endpoint: ''
      });
      setError('');
    }
  }, [show]);

  // Handle form input changes
  const handleCreateInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCreateFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Use wagmi's useWriteContract for the profile update operation
  const { writeContract: updateProfile } = useWriteContract();

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); // Clear previous errors

    // Convert degrees and awards from comma-separated strings to arrays
    const degreesArray = createFormData.degrees.split(',').map(item => item.trim());
    const awardsArray = createFormData.awards.split(',').map(item => item.trim());

    // Validate endpoint format
    const endpointRegex = /^(https?:\/\/)/;
    if (createFormData.endpoint && !endpointRegex.test(createFormData.endpoint)) {
      setError('Endpoint must start with "https://" or "http://"');
      return;
    }

    try {
      await updateProfile({
        abi: contractABI.abi,
        address: contractAddress,
        functionName: 'updateMemberInfo',
        args: [
          createFormData.name,
          createFormData.description,
          degreesArray,
          awardsArray,
          createFormData.endpoint,
        ],
      });

      setShow(false);
      setShowSuccessAlert(true); // Show success alert
      navigate('/profile');
    } catch (error) {
      console.error("Error updating profile:", error);
      setError('Failed to update profile. Please try again.');
    }
  };

  const renderTooltip = (text: string) => (
    <Tooltip id="button-tooltip">
      {text}
    </Tooltip>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Form.Group>
            <Form.Label>
              Name
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("Your Name")}>
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              name="name"
              value={createFormData.name}
              onChange={handleCreateInputChange}
              required
            />
          </Form.Group>
        );
      case 2:
        return (
          <Form.Group>
            <Form.Label>
              Description
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("Enter some information about yourself")}>
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              as="textarea"
              name="description"
              value={createFormData.description}
              onChange={handleCreateInputChange}
              required
            />
          </Form.Group>
        );
      case 3:
        return (
          <Form.Group>
            <Form.Label>
              Degrees (comma-separated)
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("List any degrees you hold.")}>
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="text"
              name="degrees"
              value={createFormData.degrees}
              onChange={handleCreateInputChange}
              placeholder="Degree1, Degree2, Degree3"
            />
          </Form.Group>
        );
      case 4:
        return (
          <Form.Group>
            <Form.Label>
              Awards (comma-separated)
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("List any awards or achievements you want to showcase.")}>
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="text"
              name="awards"
              value={createFormData.awards}
              onChange={handleCreateInputChange}
              placeholder="Award1, Award2, Award3"
            />
          </Form.Group>
        );
      case 5:
        return (
          <Form.Group>
            <Form.Label>
              Endpoint
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("Provide a full URL starting with 'https://' or 'http://' for your portfolio, social media, or other relevant profiles.")}>
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="url"
              name="endpoint"
              value={createFormData.endpoint}
              onChange={handleCreateInputChange}
              placeholder="https://example.com"
            />
          </Form.Group>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {showSuccessAlert && (
        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible>
          Profile updated successfully!
        </Alert>
      )}
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Update Your Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={handleUpdateProfile}>
            {renderStep()}
            <ProgressBar now={(currentStep / totalSteps) * 100} />
            <div className="mt-3 d-flex justify-content-between">
              <Button variant="outline-secondary" onClick={handlePrev} disabled={currentStep === 1}>
                Previous
              </Button>
              {currentStep < totalSteps ? (
                <Button variant="info" type="button" onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button variant="info" type="submit">
                  Update Profile
                </Button>
              )}
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SetProfileModal;
